import {
  endOfMonth, startOfDay, startOfMonth, subDays, subMonths,
} from 'date-fns';

export type DateRangeValue = [Date | null, Date | null];

/**
 * A user's default range is stored as one of these keys, not as two concrete
 * dates: "Last 7 days" must mean the 7 days ending today every time the report
 * opens, not the week that was on screen when the preference was saved.
 */
export type DatePresetKey =
  | 'today'
  | 'yesterday'
  | 'last_2_days'
  | 'last_3_days'
  | 'last_7_days'
  | 'last_14_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month';

export const DEFAULT_DATE_PRESET: DatePresetKey = 'last_7_days';

interface DatePreset {
  key: DatePresetKey;
  title: string;
  resolve: (today: Date) => DateRangeValue;
}

export const DATE_PRESETS: DatePreset[] = [
  { key: 'today', title: 'Today', resolve: (t) => [t, t] },
  { key: 'yesterday', title: 'Yesterday', resolve: (t) => [subDays(t, 1), subDays(t, 1)] },
  { key: 'last_2_days', title: 'Last 2 days', resolve: (t) => [subDays(t, 1), t] },
  { key: 'last_3_days', title: 'Last 3 days', resolve: (t) => [subDays(t, 2), t] },
  { key: 'last_7_days', title: 'Last 7 days', resolve: (t) => [subDays(t, 6), t] },
  { key: 'last_14_days', title: 'Last 14 days', resolve: (t) => [subDays(t, 13), t] },
  { key: 'last_30_days', title: 'Last 30 days', resolve: (t) => [subDays(t, 29), t] },
  { key: 'this_month', title: 'This month', resolve: (t) => [startOfMonth(t), t] },
  {
    key: 'last_month',
    title: 'Last month',
    resolve: (t) => [startOfMonth(subMonths(t, 1)), endOfMonth(subMonths(t, 1))],
  },
];

export function isDatePresetKey(v: unknown): v is DatePresetKey {
  return typeof v === 'string' && DATE_PRESETS.some((p) => p.key === v);
}

/** Falls back to the shared default so a stale preference still renders a report. */
export function resolveDatePreset(key: DatePresetKey | undefined | null, today = startOfDay(new Date())): DateRangeValue {
  const preset = DATE_PRESETS.find((p) => p.key === key)
    ?? DATE_PRESETS.find((p) => p.key === DEFAULT_DATE_PRESET)!;
  return preset.resolve(today);
}

export function getPresetRanges(): { key: DatePresetKey; title: string; dateRange: DateRangeValue }[] {
  const today = startOfDay(new Date());
  return DATE_PRESETS.map((p) => ({ key: p.key, title: p.title, dateRange: p.resolve(today) }));
}
